import type { AdminPlanStatsResponse, AdminUserStatsResponse } from '@pulsify/shared/dto'
import type { UseQueryResult } from '@tanstack/react-query'
import { useState } from 'react'
import {
  useAdminLiveRoomGuestStats,
  useAdminPlanStats,
  useAdminUserStats,
} from '~/entities/stats/model/use-admin-stats'
import { usePublicStats } from '~/entities/stats/model/use-public-stats'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { METRIC_COLORS } from '~/shared/styles/chart-palette'
import { ratio } from './stats-derive'
import {
  ActivityFigures,
  AssistantFigures,
  ChatFigures,
  InviteFigures,
  InviteRate,
  JoinerFigures,
  PlatformFigures,
  UpcomingFigures,
} from './stats-details'
import { formatCount, formatShare, formatSnapshotTime, oldestSnapshot } from './stats-format'
import { HeroChart, type HeroMetric, type HeroMetricKey, HeroTiles } from './stats-overview'
import { RoomGuestFigures } from './stats-room-guests'
import { StatCard, StatsError, StatsQueryState } from './stats-tiles'

function queryState(query: UseQueryResult): HeroMetric['state'] {
  if (query.data !== undefined) return 'ready'
  return query.isError ? 'error' : 'loading'
}

function heroMetrics(
  users: UseQueryResult<AdminUserStatsResponse>,
  plans: UseQueryResult<AdminPlanStatsResponse>,
  locale: string,
): HeroMetric[] {
  const u = users.data
  const p = plans.data
  const count = (value: number) => formatCount(value, locale)
  return [
    {
      key: 'active',
      label: m.statsSeriesActive(),
      explain: m.statsExplainActive(),
      color: METRIC_COLORS.views,
      state: queryState(users),
      value: u?.active.last7d,
      detail: u
        ? m.statsShareOfAccounts({
            share: formatShare(ratio(u.active.last7d, u.accounts.total), locale),
          })
        : undefined,
      daily: u?.daily.map((entry) => ({ day: entry.day, value: entry.active })),
    },
    {
      key: 'signups',
      label: m.statsSeriesSignups(),
      explain: m.statsExplainSignups(),
      color: METRIC_COLORS.interactions,
      state: queryState(users),
      value: u?.accounts.newLast7d,
      detail: u ? m.statsAccountsTotal({ count: count(u.accounts.total) }) : undefined,
      daily: u?.daily.map((entry) => ({ day: entry.day, value: entry.signups })),
    },
    {
      key: 'plansCreated',
      label: m.statsSeriesPlansCreated(),
      explain: m.statsExplainPlans(),
      color: METRIC_COLORS.checkIns,
      state: queryState(plans),
      value: p?.plans.createdLast7d,
      detail: p ? m.statsCountIn30Days({ count: count(p.plans.createdLast30d) }) : undefined,
      daily: p?.daily.map((entry) => ({ day: entry.day, value: entry.plansCreated })),
    },
    {
      key: 'peopleJoined',
      label: m.statsSeriesPeopleJoined(),
      explain: m.statsExplainJoined(),
      color: METRIC_COLORS.likes,
      state: queryState(plans),
      value: p?.people.joinedLast7d,
      detail: p ? m.statsGuestsAmongThem({ count: count(p.people.guestsJoinedLast7d) }) : undefined,
      daily: p?.daily.map((entry) => ({ day: entry.day, value: entry.peopleJoined })),
    },
  ]
}

export function StatsPage() {
  const locale = getLocale()
  const users = useAdminUserStats()
  const plans = useAdminPlanStats()
  const platform = usePublicStats()
  const roomGuests = useAdminLiveRoomGuestStats()
  const [selected, setSelected] = useState<HeroMetricKey>('active')
  const snapshot = oldestSnapshot([
    users.data?.generatedAt,
    plans.data?.generatedAt,
    roomGuests.data?.generatedAt,
  ])
  const metrics = heroMetrics(users, plans, locale)
  const current = metrics.find((metric) => metric.key === selected) ?? metrics[0]
  const currentQuery = selected === 'active' || selected === 'signups' ? users : plans

  return (
    <div className="px-4 py-8 sm:px-8 sm:py-16">
      <div className="mx-auto max-w-6xl space-y-8">
        <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
          <div className="space-y-1.5">
            <h1 className="font-semibold text-3xl text-foreground tracking-tight">
              {m.statsTitle()}
            </h1>
            <p className="text-muted-foreground text-sm">{m.statsDescription()}</p>
          </div>
          {snapshot && (
            <p className="inline-flex items-center gap-2 text-muted-foreground text-xs tabular-nums">
              <span aria-hidden className="size-1.5 rounded-full bg-[var(--metric-up)]" />
              {m.statsAsOf({ time: formatSnapshotTime(snapshot, locale) })}
            </p>
          )}
        </header>

        <section className="flex flex-col gap-3">
          <p className="text-muted-foreground text-xs">{m.statsHeroCaption()}</p>
          <HeroTiles metrics={metrics} selected={selected} onSelect={setSelected} locale={locale} />
          <HeroChart
            metric={current}
            locale={locale}
            errorSlot={<StatsError query={currentQuery} />}
          />
        </section>

        <div className="grid gap-4 md:grid-cols-2">
          <StatCard title={m.statsActivityTitle()} hint={m.statsActivityHint()}>
            <StatsQueryState query={users}>
              {(stats) => <ActivityFigures stats={stats} locale={locale} />}
            </StatsQueryState>
          </StatCard>

          <StatCard title={m.statsJoinersTitle()} hint={m.statsJoinersHint()}>
            <StatsQueryState query={plans}>
              {(stats) => <JoinerFigures stats={stats} locale={locale} />}
            </StatsQueryState>
          </StatCard>

          <StatCard
            title={m.statsUpcomingCaption()}
            hint={m.statsUpcomingHint()}
            className="md:col-span-2"
          >
            <StatsQueryState query={plans}>
              {(stats) => <UpcomingFigures stats={stats} locale={locale} />}
            </StatsQueryState>
          </StatCard>

          <StatCard
            title={m.statsInvitesCaption()}
            hint={m.statsInvitesHint()}
            aside={plans.data && <InviteRate stats={plans.data} locale={locale} />}
          >
            <StatsQueryState query={plans}>
              {(stats) => <InviteFigures stats={stats} locale={locale} />}
            </StatsQueryState>
          </StatCard>

          <StatCard title={m.statsChatTitle()} hint={m.statsChatHint()}>
            <StatsQueryState query={plans}>
              {(stats) => <ChatFigures stats={stats} locale={locale} />}
            </StatsQueryState>
          </StatCard>

          <StatCard
            title={m.statsAssistantCaption()}
            hint={m.statsAssistantHint()}
            className="md:col-span-2"
          >
            <StatsQueryState query={plans}>
              {(stats) => <AssistantFigures stats={stats} locale={locale} />}
            </StatsQueryState>
          </StatCard>

          <StatCard
            title={m.statsRoomGuestsTitle()}
            hint={m.statsRoomGuestsHint()}
            className="md:col-span-2"
          >
            <StatsQueryState query={roomGuests}>
              {(stats) => <RoomGuestFigures stats={stats} locale={locale} />}
            </StatsQueryState>
          </StatCard>

          <StatCard
            title={m.statsPlatformSection()}
            hint={m.statsPlatformHint()}
            className="md:col-span-2"
          >
            <StatsQueryState query={platform}>
              {(stats) => (
                <PlatformFigures stats={stats} guests={users.data?.guests} locale={locale} />
              )}
            </StatsQueryState>
          </StatCard>
        </div>
      </div>
    </div>
  )
}
